import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, Edit, Trash2, TrendingUp, GraduationCap, Combine, Power, RotateCcw, ChevronDown, ChevronUp, ChevronRight,
  CheckCircle2, AlertTriangle, XCircle, Gauge,
} from "lucide-react";
import { API } from "../../../App";
import PilotFlightsDialog from "./PilotFlightsDialog";
import ValidityTimeline from "./ValidityTimeline";
import CardDocuments from "../../../components/CardDocuments";
import { buildPilotView, isNumeroProvisorio, LICENSE_LABEL } from "../../../lib/pilotView";
import { fmtDMY } from "../../../lib/instructorView";

// Tons: só os que falham ou estão a acabar ganham cor; "info" é progresso em curso e "neutral" só informa (sem ícone)
const TONE = {
  ok:      { dot: "bg-emerald-400", tile: "border-slate-700 bg-slate-900/50",       icon: "text-emerald-400", text: "text-emerald-300", bar: "bg-emerald-500", Icon: CheckCircle2 },
  warn:    { dot: "bg-amber-400",   tile: "border-amber-500/50 bg-amber-500/10",    icon: "text-amber-400",   text: "text-amber-300",   bar: "bg-amber-400",   Icon: AlertTriangle },
  bad:     { dot: "bg-red-500",     tile: "border-red-500/60 bg-red-500/10",        icon: "text-red-400",     text: "text-red-300",     bar: "bg-red-500",     Icon: XCircle },
  info:    { dot: "bg-sky-400",     tile: "border-sky-500/30 bg-sky-500/[0.06]",    icon: "text-sky-400",     text: "text-sky-300",     bar: "bg-sky-400",     Icon: Gauge },
  neutral: { dot: "bg-slate-500",   tile: "border-slate-700 bg-slate-900/50",       icon: "text-slate-500",   text: "text-slate-400",   bar: "bg-slate-500",   Icon: null },
};

const KIND_CHIP = {
  ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  warn: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  grounded: "bg-red-500/15 text-red-300 border-red-500/40",
  inactive: "bg-slate-700/60 text-slate-300 border-slate-600/50",
};

function daysText(r) {
  if (r.days == null) return "";
  return r.days < 0 ? `expirou há ${-r.days} ${-r.days === 1 ? "dia" : "dias"}` : `${r.days} ${r.days === 1 ? "dia" : "dias"}`;
}

function Block({ title, children, testid }) {
  return (
    <div className="mt-3 pt-3 border-t border-slate-700/60" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function Bar({ label, have, need, pct, ok, color, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-[12px] text-slate-300 mb-1">
        <span>{label}</span>
        <span className="font-mono">{have} <span className="text-slate-500">de {need}</span> {ok && <span className="text-emerald-300">✓</span>}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function PilotCard({ pilot, expanded = false, onToggle, onEdit, onDelete, onToggleActive, onMerge, onPromoted, docCount = 0 }) {
  const [showFlights, setShowFlights] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const v = useMemo(() => buildPilotView(pilot), [pilot]);
  const isInactive = pilot.active === false;
  const isStudent = v.pilotKind === "student";
  const isAvulso = v.pilotKind === "avulso";
  const isRegular = v.pilotKind === "regular";
  const isPromoted = !!pilot.promoted_to_instructor_id;

  const promoteToRegular = async () => {
    if (!window.confirm(`Promover ${pilot.name} a Piloto Regular?\n\nO perfil de ${isStudent ? "Aluno" : "Piloto Avulso"} será alterado para Piloto Regular. Todo o histórico é preservado.`)) return;
    setPromoting(true);
    try {
      await axios.post(`${API}/di/pilots/${pilot.id}/promote-to-regular`, {}, { withCredentials: true });
      toast.success(`${pilot.name} promovido a Piloto Regular`);
      onPromoted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro na promoção"); }
    finally { setPromoting(false); }
  };

  const promoteToInstructor = async () => {
    if (!window.confirm(`Promover ${pilot.name} a Instrutor?\n\nSerá criada uma nova ficha de Instrutor ligada ao histórico de voos actual.\nA ficha de Piloto ficará arquivada (oculta mas preservada).\n\nO instrutor ficará com estado Restrito (R) — preencha as licenças e qualificações após a promoção.`)) return;
    setPromoting(true);
    try {
      await axios.post(`${API}/di/pilots/${pilot.id}/promote-to-instructor`, {}, { withCredentials: true });
      toast.success(`${pilot.name} promovido a Instrutor. Preencha as licenças e qualificações na secção Instrutores.`);
      onPromoted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro na promoção"); }
    finally { setPromoting(false); }
  };

  const cardClass = isInactive
    ? "rounded-lg border border-slate-700 bg-slate-900/40 p-4 opacity-60"
    : v.kind === "grounded"
      ? "rounded-lg border-2 border-red-500/60 bg-red-500/[0.04] p-4 ring-1 ring-red-500/20"
      : v.kind === "warn"
        ? "rounded-lg border border-amber-500/40 bg-amber-500/[0.03] p-4"
        : "rounded-lg border border-cockpit-border bg-slate-900/40 p-4";
  const tileCols = v.tiles.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3";
  const badge = "text-[9px] uppercase font-bold px-1.5 py-0.5 rounded tracking-wide shrink-0";
  const go = () => onEdit?.();

  return (
    <div className={cardClass} data-testid="di-pilot-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex-1 min-w-[13rem] text-left" data-testid={`di-pilot-toggle-${pilot.pu_number}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-base text-slate-100 truncate" data-testid="di-pilot-name">{pilot.name}</span>
            <span className="font-mono text-xs text-slate-400" data-testid="di-pilot-pu">{LICENSE_LABEL[pilot.license_type] || pilot.license_type || "PU"} {isNumeroProvisorio(pilot.pu_number) ? "provisório (cartão por emitir)" : pilot.pu_number}</span>
            {isStudent && <span className={`${badge} bg-emerald-500/20 text-emerald-300`} data-testid="di-pilot-kind-student">ALUNO</span>}
            {isAvulso && <span className={`${badge} bg-slate-700 text-slate-300`} data-testid="di-pilot-kind-avulso">AVULSO</span>}
            {isInactive && <span className={`${badge} bg-amber-500/20 text-amber-300`} data-testid="di-pilot-inactive-badge">INACTIVO</span>}
            {isPromoted && <span className={`${badge} bg-purple-500/20 text-purple-300`} title="Promovido a Instrutor — ficha arquivada" data-testid="di-pilot-promoted-badge"><GraduationCap className="inline w-3 h-3 mr-0.5" />INSTRUTOR</span>}
            {pilot.bell_notifications_enabled === false && <span className={`${badge} bg-slate-700 text-slate-300`} title="Alertas deste piloto silenciados no sininho do admin" data-testid="di-pilot-bell-muted">🔕 Sininho off</span>}
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-semibold ${KIND_CHIP[v.kind]}`} data-testid="di-pilot-status">{v.phrase}</span>
            <span className="text-[12px] text-slate-300 min-w-0" data-testid="di-pilot-reason">{v.reason}</span>
          </div>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          <CardDocuments ownerKind="pilot" ownerId={pilot.id} count={docCount} />
          <button onClick={() => setShowFlights(true)} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-blue-300" title="Voos" data-testid="di-pilot-flights-btn"><BookOpen className="w-3.5 h-3.5" /></button>
          <button onClick={onEdit} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-orange-300" title="Editar" data-testid="di-pilot-edit-btn"><Edit className="w-3.5 h-3.5" /></button>
          {(isStudent || isAvulso) && !isPromoted && <button onClick={promoteToRegular} disabled={promoting} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-300 disabled:opacity-50" title={isStudent ? "Promover Aluno a Piloto Regular" : "Promover Avulso a Piloto Regular"} data-testid="di-pilot-promote-regular-btn"><TrendingUp className="w-3.5 h-3.5" /></button>}
          {isRegular && !isPromoted && <button onClick={promoteToInstructor} disabled={promoting} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-purple-300 disabled:opacity-50" title="Promover a Instrutor" data-testid="di-pilot-promote-instructor-btn"><GraduationCap className="w-3.5 h-3.5" /></button>}
          {onMerge && <button onClick={onMerge} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-purple-300" title="Fundir com outro piloto" data-testid="di-pilot-merge-btn"><Combine className="w-3.5 h-3.5" /></button>}
          <button onClick={onToggleActive} className={`p-1 rounded hover:bg-slate-800 ${isInactive ? "text-emerald-400 hover:text-emerald-300" : "text-slate-400 hover:text-amber-300"}`} title={isInactive ? "Reactivar" : "Desactivar"} data-testid="di-pilot-toggle-active">{isInactive ? <RotateCcw className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}</button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400" title="Eliminar" data-testid="di-pilot-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={onToggle} className="p-1 text-slate-500" aria-label={expanded ? "Fechar detalhes" : "Abrir detalhes"} data-testid="di-pilot-chevron">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Os mosaicos, sempre à vista: nome pequeno, valor em destaque, o que falta por baixo */}
      <div className={`grid grid-cols-2 ${tileCols} gap-2 mt-3`} data-testid="di-pilot-tiles">
        {v.tiles.map((t, idx) => {
          const tn = TONE[t.tone];
          // com um número ímpar de mosaicos, o último ocupa a linha toda no telemóvel (2 colunas) em vez de ficar sozinho a meio
          const lonely = v.tiles.length % 2 === 1 && idx === v.tiles.length - 1 ? "col-span-2 md:col-span-1" : "";
          return (
            <div key={t.key} className={`rounded-lg border px-2.5 py-2 min-w-0 ${lonely} ${tn.tile}`} data-testid={`di-pilot-tile-${t.key}`} title={`${t.label}: ${t.sub}`}>
              <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{t.short}</span>
                {tn.Icon && <tn.Icon className={`w-3.5 h-3.5 shrink-0 ${tn.icon}`} aria-hidden="true" />}
              </div>
              <div className="font-mono text-[13px] leading-tight text-slate-100 mt-0.5 break-words">{t.value}</div>
              <div className={`text-[11px] leading-snug ${t.tone === "ok" || t.tone === "neutral" ? "text-slate-400" : tn.text}`}>{t.sub}</div>
              {t.key === "medical" && pilot.medical_cert_number && (
                <div className="text-[10px] text-slate-500 mt-0.5" data-testid="di-pilot-tile-medical-number">Nº médico {pilot.medical_cert_number}</div>
              )}
            </div>
          );
        })}
      </div>

      {expanded && (
        <div data-testid="di-pilot-details">
          <Block title="Dados do piloto" testid="di-pilot-data">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="px-3 py-2 rounded border border-slate-700 bg-slate-900/50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Tipo de ficha</div>
                <div className="font-mono text-sm text-slate-100">{v.info.kindLabel}</div>
              </div>
              <div className="px-3 py-2 rounded border border-slate-700 bg-slate-900/50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Licença</div>
                <div className="font-mono text-sm text-slate-100">{v.info.license}</div>
              </div>
              <div className="px-3 py-2 rounded border border-slate-700 bg-slate-900/50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Nº do certificado médico</div>
                <div className="font-mono text-sm text-slate-100">{v.info.medicalNumber || "—"}</div>
              </div>
            </div>
            {v.info.notes && <div className="mt-2 text-[12px] text-slate-300 whitespace-pre-line" data-testid="di-pilot-notes">{v.info.notes}</div>}
          </Block>

          <Block title="Horas" testid="di-pilot-hours">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {v.metrics.map((m) => (
                <div key={m.label} className="px-3 py-2 rounded border border-slate-700 bg-slate-900/50">
                  <div className="font-mono text-base text-slate-100">{m.value}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{m.label}</div>
                </div>
              ))}
            </div>
          </Block>

          {v.student && (
            <Block title="Instrução prática (para a prova)" testid="di-pilot-student-progress">
              <div className="space-y-2.5 max-w-md">
                <Bar {...v.student.bars[0]} color={TONE.ok.bar} testId="di-pilot-progress-dc" />
                <Bar {...v.student.bars[1]} color="bg-blue-400" testId="di-pilot-progress-solo" />
              </div>
              {v.student.ready && (
                <div className="mt-2 inline-block text-[11px] px-2 py-0.5 rounded border font-semibold bg-emerald-500/15 text-emerald-300 border-emerald-500/40" data-testid="di-pilot-student-ready">Apto para a prova ✓</div>
              )}
            </Block>
          )}

          <Block title="Revalidações" testid="di-pilot-revalidations">
            {pilot.proof_check_date && (
              <p className="text-[11px] text-slate-300 mb-2" data-testid="di-pilot-proof-check">Prova com examinador feita em {fmtDMY(pilot.proof_check_date)} · aguarda a emissão da ANAC; a validade é introduzida quando chegar o documento.</p>
            )}
            <div className="rounded border border-slate-700/60 overflow-hidden">
              {v.revalidations.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={go}
                  title="Abrir a ficha para atualizar"
                  className="w-full text-left flex items-start gap-2.5 px-3 py-2 border-t border-slate-700/60 first:border-t-0 hover:bg-slate-800/60 focus:outline-none focus-visible:bg-slate-800/60 transition-colors"
                  data-testid={`di-pilot-reval-${r.key}`}
                >
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${TONE[r.tone].dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium text-slate-100">{r.title}</div>
                    <div className="text-[11px] text-slate-400 leading-snug">{r.rule}</div>
                    {r.progress && <div className="text-[11px] text-slate-200 mt-0.5">{r.progress}</div>}
                    {r.pct != null && (
                      <div className="mt-1 h-1.5 rounded-full bg-slate-800 overflow-hidden max-w-[260px]">
                        <div className={`h-full rounded-full ${TONE[r.tone].bar}`} style={{ width: `${r.pct}%` }} />
                      </div>
                    )}
                  </div>
                  <span className={`text-[11px] whitespace-nowrap pt-0.5 ${TONE[r.tone].text}`}>{daysText(r)}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-1" aria-hidden="true" />
                </button>
              ))}
            </div>
          </Block>

          <Block title="Validades nos próximos 12 meses" testid="di-pilot-timeline">
            <ValidityTimeline entries={v.timeline} />
          </Block>
        </div>
      )}
      {showFlights && <PilotFlightsDialog pilot={pilot} onClose={() => setShowFlights(false)} />}
    </div>
  );
}
